import {
  Form,
  Button,
  Dropdown,
} from "react-bootstrap";
import { useState, useEffect } from "react";
import {
  EditIcon,
  FileListIcon,
  FilterTwoIcon,
  TrashIcon,
  ExportIcon,
  AddCircleIcon,
} from "../Icons";
import TableComponent from "../TableComponent";
import DotsHorizontalIcon from "mdi-react/DotsHorizontalIcon";
import EyeIcon from "mdi-react/EyeIcon";
import CachedIcon from "mdi-react/CachedIcon";
import {
  FaClock,
  FaPlay,
  FaStop,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
} from "react-icons/fa";
import ConfirmDialog from "../ConfirmDialogue";
import { useAuth } from "../../hooks/useAuth";
import queryString from "query-string";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import useDebounce, { useQueryParams } from "../../utils/hooks";
import ModalLoader from "../utils/ModalLoader";
import TripModal from "./TripModal";
import TripDetailModal from "./TripDetailModal";

export default function FleetTrips() {
  const { backendUrl } = useAuth();
  const queryClient = useQueryClient();
  const [showFilter, setShowFilter] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showTripModal, setShowTripModal] = useState(false);
  const [isMainLoading, setIsMainLoading] = useState(false);
  const [editTrip, setEditTrip] = useState(null);

  // Selector popovers (removed as now in TripModal)

  const initialFilterParams = {
    q: "",
    status: "",
    vehicle_id: "",
    driver_id: "",
    route_id: "",
    from: "",
    to: "",
  };

  const [queryParams, setQueryParams] = useQueryParams({
    page: 1,
    limit: 20,
    ...initialFilterParams,
  });

  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);

  useEffect(() => {
    setQueryParams({ ...queryParams, ...debouncedFilterParams });
  }, [debouncedFilterParams]);

  // Removed newTrip state as now handled in TripModal

  const statusOptions = [
    { value: "planned", label: "Planned", icon: FaClock },
    { value: "in_progress", label: "In Progress", icon: FaPlay },
    { value: "completed", label: "Completed", icon: FaCheck },
    { value: "cancelled", label: "Cancelled", icon: FaTimes },
    { value: "delayed", label: "Delayed", icon: FaExclamationTriangle },
  ];

  // Fetch trips
  const fetchTrips = async (params) => {
    const response = await fetch(
      `${backendUrl}/api/fleet/trips?${queryString.stringify(params)}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    const result = await response.json();
    return result.data;
  };

  const {
    data = { trips: [], count: 0, page: 1, limit: 20 },
    refetch,
    isLoading,
  } = useQuery(["FLEET_TRIPS", queryParams], () => fetchTrips(queryParams), {
    keepPreviousData: true,
    staleTime: 0,
  });

  // Fetch vehicles for dropdown
  const fetchVehicles = async () => {
    const response = await fetch(
      `${backendUrl}/api/fleet/vehicles?page=1&limit=100&status=available`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    const result = await response.json();
    return result.data.vehicles;
  };

  const { data: vehicles = [] } = useQuery(
    ["VEHICLES_DROPDOWN"],
    fetchVehicles,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Fetch drivers for dropdown
  const fetchDrivers = async () => {
    const response = await fetch(
      `${backendUrl}/api/fleet/drivers?page=1&limit=100&status=`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    const result = await response.json();
    return result.data.drivers;
  };

  const { data: drivers = [] } = useQuery(["DRIVERS_DROPDOWN"], fetchDrivers, {
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch routes for dropdown
  const fetchRoutes = async () => {
    const response = await fetch(
      `${backendUrl}/api/fleet/routes?page=1&limit=100&isActive=true`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    const result = await response.json();
    return result.data.routes;
  };

  const { data: routes = [] } = useQuery(["ROUTES_DROPDOWN"], fetchRoutes, {
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create trip mutation
  const createTripMutation = useMutation(
    async (tripData) => {
      const response = await fetch(`${backendUrl}/api/fleet/trips`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(tripData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      return response.json();
    },
    {
      onSuccess: () => {
        toast.success("Trip created successfully");
        queryClient.invalidateQueries(["FLEET_TRIPS"]);
        setShowTripModal(false);
        setEditTrip(null);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  // Update trip mutation
  const updateTripMutation = useMutation(
    async ({ tripId, tripData }) => {
      const response = await fetch(`${backendUrl}/api/fleet/trips/${tripId}`, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(tripData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      return response.json();
    },
    {
      onSuccess: () => {
        toast.success("Trip updated successfully");
        queryClient.invalidateQueries(["FLEET_TRIPS"]);
        setShowTripModal(false);
        setEditTrip(null);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  // Update trip status mutation
  const updateTripStatusMutation = useMutation(
    async ({ tripId, status }) => {
      const response = await fetch(
        `${backendUrl}/api/fleet/trips/${tripId}/status`,
        {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ status }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      return response.json();
    },
    {
      onSuccess: () => {
        toast.success("Trip status updated successfully");
        queryClient.invalidateQueries(["FLEET_TRIPS"]);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterParams({
      ...filterParams,
      [name]: value,
    });
  };

  const resetFilters = () => {
    setFilterParams(initialFilterParams);
    setQueryParams({
      ...queryParams,
      ...initialFilterParams,
    });
  };

  const handleSearch = () => {
    setQueryParams({
      ...queryParams,
      ...filterParams,
    });
  };

  const handleViewDetails = (trip) => {
    setSelectedTrip(trip);
    setShowDetailsModal(true);
  };

  const handleEdit = (trip) => {
    setEditTrip(trip);
    setShowTripModal(true);
  };

  const handleStatusChange = async (trip, newStatus) => {
    const confirmed = await ConfirmDialog({
      title: "Update Trip Status",
      description: `Are you sure you want to change the status of trip "${
        trip.tripNumber
      }" to "${newStatus.replace("_", " ")}"?`,
    });

    if (confirmed) {
      updateTripStatusMutation.mutate({
        tripId: trip.trip_id,
        status: newStatus,
      });
    }
  };

  const handleDelete = async (trip) => {
    const confirmed = await ConfirmDialog({
      title: "Delete Trip",
      description: `Are you sure you want to delete trip "${trip.tripNumber}"?`,
    });

    if (confirmed) {
      // Implement delete mutation if needed
      toast.info("Delete functionality not implemented yet");
    }
  };

  // Removed handleNewTripChange and resetNewTrip as now handled in TripModal

  const handleCreateTrip = async (values) => {
    const tripData = { ...values };
    if (values.status === "in_progress") {
      tripData.actualStartTime =
        tripData.actualStartTime || new Date().toISOString();
    }
    if (
      await ConfirmDialog({
        title: "Create new Trip",
        description: "Are you sure you want to create a new trip?",
      })
    )
      createTripMutation.mutate(tripData);
  };

  // Removed handleEditTripChange as now handled in TripModal

  const handleUpdateTrip = (values) => {
    const tripData = { ...values };
    if (values.status === "in_progress" && !editTrip.actualStartTime) {
      tripData.actualStartTime = new Date().toISOString();
    }
    updateTripMutation.mutate({
      tripId: editTrip.trip_id,
      tripData,
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusClass = (status) => {
    const statusMap = {
      planned: "view_status_pending",
      in_progress: "view_status_approved",
      completed: "view_status_all",
      cancelled: "view_status_reject",
      delayed: "view_status_partly",
    };
    return statusMap[status] || "";
  };

  const getStatusIcon = (status) => {
    const statusOption = statusOptions.find((opt) => opt.value === status);
    return statusOption ? statusOption.icon : FaClock;
  };

  const tableHead = () => (
    <thead>
      <tr>
        <th />
        <th>Trip Number</th>
        <th>Vehicle</th>
        <th>Driver</th>
        <th>Route</th>
        <th>Start Time</th>
        <th>End Time</th>
        <th>Distance</th>
        <th>Status</th>
      </tr>
    </thead>
  );

  const displayTableData = (trip) => {
    const StatusIcon = getStatusIcon(trip.status);

    return (
      <>
        <td>
          <Dropdown>
            <Dropdown.Toggle
              variant=""
              className="bg-white border-0"
              bsPrefix="print more"
              style={{ height: "3px" }}
            >
              <DotsHorizontalIcon size={18} />
            </Dropdown.Toggle>
            <Dropdown.Menu
              popperConfig={{ strategy: "fixed" }}
              renderOnMount
              className="dropdown-with-icons"
            >
              <Dropdown.Item onClick={() => handleViewDetails(trip)}>
                <EyeIcon size={16} className="me-2" />
                View Details
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  if (
                    !["planned", "in_progress", "delayed"].includes(trip.status)
                  )
                    return toast.warn("Cannot edit a completed trip");
                  handleEdit(trip);
                }}
              >
                <EditIcon size={16} className="me-2" />
                Edit Trip
              </Dropdown.Item>
              <Dropdown.Item>
                <FileListIcon color={"black"} size={16} className="me-2" />
                Documents
              </Dropdown.Item>
              <Dropdown.Divider />
              {trip.status === "planned" && (
                <Dropdown.Item
                  onClick={() => handleStatusChange(trip, "in_progress")}
                >
                  <FaPlay size={16} className="me-2" />
                  Start Trip
                </Dropdown.Item>
              )}
              {trip.status === "in_progress" && (
                <Dropdown.Item
                  onClick={() => handleStatusChange(trip, "completed")}
                >
                  <FaStop size={16} className="me-2" />
                  End Trip
                </Dropdown.Item>
              )}
              {trip.status !== "completed" && trip.status !== "cancelled" && (
                <Dropdown.Item
                  onClick={() => handleStatusChange(trip, "cancelled")}
                >
                  <FaTimes size={16} className="me-2" />
                  Cancel Trip
                </Dropdown.Item>
              )}
              <Dropdown.Divider />
              <Dropdown.Item
                className="text-danger"
                onClick={() => handleDelete(trip)}
              >
                <TrashIcon size={16} className="me-2" />
                Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </td>
        <td className="fw-semibold">{trip.tripNumber}</td>
        <td>
          {trip.vehicle ? (
            <div>
              <div className="fw-semibold">{trip.vehicle.fleetNumber}</div>
              <small className="text-muted">
                {trip.vehicle.make} {trip.vehicle.model}
              </small>
            </div>
          ) : (
            "N/A"
          )}
        </td>
        <td>
          {trip.driver ? (
            <div>
              <div className="fw-semibold">{trip.driver.employee_id}</div>
              <small className="text-muted">
                {trip?.driver?.employee?.Name || trip.driver.licenseNumber}
              </small>
            </div>
          ) : (
            "N/A"
          )}
        </td>
        <td>
          {trip.route ? (
            <div>
              <div className="fw-semibold">{trip.route.name}</div>
              <small className="text-muted">
                {trip.route.originAddress} → {trip.route.destinationAddress}
              </small>
            </div>
          ) : (
            "N/A"
          )}
        </td>
        <td>{formatDateTime(trip.plannedStartTime)}</td>
        <td>{formatDateTime(trip.plannedEndTime)}</td>
        <td>{trip.plannedDistance ? `${trip.plannedDistance} km` : "N/A"}</td>
        <td>
          <div className={`px-2 ${getStatusClass(trip.status)}`}>
            <StatusIcon className="me-1" />
            {trip.status.replace("_", " ").toUpperCase()}
          </div>
        </td>
      </>
    );
  };

  return (
    <section className="invoice-wrapped">
      <main className="cash-book">
        <div className="content">
          <div className="content-main">
            {/* Filter Sidebar */}
            <div className={`filter-area ${showFilter ? "open" : "close"}`}>
              <div className="filter-header">
                <h2>
                  <FilterTwoIcon size={20} />
                  Filters
                </h2>
                <Button
                  variant="white"
                  onClick={() => setShowFilter(!showFilter)}
                >
                  ×
                </Button>
              </div>
              <div className="filter-body">
                <Form onSubmit={(e) => e.preventDefault()}>
                  <div className="body">
                    <Form.Group className="mb-2-5">
                      <Form.Label>Search</Form.Label>
                      <Form.Control
                        name="q"
                        placeholder="Trip Number, Purpose"
                        value={filterParams.q}
                        onChange={handleFilterChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-2-5">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        name="status"
                        value={filterParams.status}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Statuses</option>
                        {statusOptions.map((opt) => (
                          <option key={opt.value} value={opt.label}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-2-5">
                      <Form.Label>Vehicle</Form.Label>
                      <Form.Select
                        name="vehicle_id"
                        value={filterParams.vehicle_id}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Vehicles</option>
                        {vehicles.map((vehicle) => (
                          <option
                            key={vehicle.vehicle_id}
                            value={vehicle.vehicle_id}
                          >
                            {vehicle.fleetNumber} - {vehicle.make}{" "}
                            {vehicle.model}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-2-5">
                      <Form.Label>Driver</Form.Label>
                      <Form.Select
                        name="driver_id"
                        value={filterParams.driver_id}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Drivers</option>
                        {drivers.map((driver) => (
                          <option
                            key={driver.driver_id}
                            value={driver.driver_id}
                          >
                            {driver.employee_id} - {driver.licenseNumber}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-2-5">
                      <Form.Label>Route</Form.Label>
                      <Form.Select
                        name="route_id"
                        value={filterParams.route_id}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Routes</option>
                        {routes.map((route) => (
                          <option key={route.route_id} value={route.route_id}>
                            {route.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-2-5">
                      <Form.Label>From Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="from"
                        value={filterParams.from}
                        onChange={handleFilterChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-2-5">
                      <Form.Label>To Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="to"
                        value={filterParams.to}
                        onChange={handleFilterChange}
                      />
                    </Form.Group>
                  </div>

                  <div className="buttons rounded">
                    <Button onClick={resetFilters} variant="white">
                      Reset
                    </Button>
                    <Button onClick={handleSearch} variant="primary">
                      Search
                    </Button>
                  </div>
                </Form>
              </div>
            </div>

            {/* Main Content */}
            <div className="content-body">
              <header className="text-nowrap">
                <h1>
                  {!showFilter && (
                    <button
                      onClick={() => setShowFilter(!showFilter)}
                      className="btn filter"
                    >
                      <FilterTwoIcon size={20} />
                    </button>
                  )}
                  Fleet Trips
                  <button
                    title="Refresh"
                    onClick={() => refetch()}
                    className="btn text-primary mx-3"
                  >
                    <CachedIcon />
                  </button>
                </h1>
                <div className="actions d-flex align-items-baseline gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowTripModal(true)}
                  >
                    <AddCircleIcon className="me-1" />
                    Add Trip
                  </Button>
                  <div className="btn bg print">
                    Export
                    <ExportIcon className="ms-2" />
                  </div>
                </div>
              </header>

              <div className="px-md-4">
                <TableComponent
                  responsive
                  borderless
                  striped
                  className="product-table text-nowrap"
                  tableHeadsFunction={tableHead}
                  tableDataRowFunction={displayTableData}
                  mainDataArray={data.trips}
                />
              </div>

              {data.trips.length === 0 && (
                <div className="d-flex justify-content-center">
                  <div className="info text-center">
                    <h2 className="my-2">No Trips Available</h2>
                  </div>
                </div>
              )}

              {/* Pagination */}
              <div className="d-flex justify-content-between px-3 align-items-center pagination">
                <div className="pagination_left">
                  <p className="m-0 p-0">Show</p>
                  <select
                    value={queryParams.limit}
                    name="limit"
                    className="form-select"
                    onChange={(e) =>
                      setQueryParams({
                        ...queryParams,
                        limit: e.target.value,
                        page: 1,
                      })
                    }
                  >
                    <option value="10">10 rows</option>
                    <option value="20">20 rows</option>
                    <option value="30">30 rows</option>
                    <option value="40">40 rows</option>
                    <option value="50">50 rows</option>
                    <option value="100">100 rows</option>
                  </select>
                </div>
                <p className="m-0">
                  Page {data.page} of {Math.ceil(data.count / data.limit)} (
                  {data.count} total)
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Trip Modal */}
        <TripModal
          show={showTripModal}
          onHide={() => {
            setShowTripModal(false);
            setEditTrip(null);
          }}
          isEdit={!!editTrip}
          editTrip={editTrip}
          onSubmit={editTrip ? handleUpdateTrip : handleCreateTrip}
          isLoading={
            createTripMutation.isLoading || updateTripMutation.isLoading
          }
        />
          <TripDetailModal
            backendUrl={backendUrl}
            trip={selectedTrip}
            show={showDetailsModal}
            onHide={() => setShowDetailsModal(false)}
          />
      </main>
      <ModalLoader show={isLoading || isMainLoading} />
    </section>
  );
}
